import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, RotateCcw } from 'lucide-react';
import { packagesAPI } from '../../api/packages';
import { showSuccess, showError } from '../../utils/toast';
import './PackagesManager.css';

// ── small helpers ─────────────────────────────────────────────────────────────

function emptyPackage(popular = false) {
  return { name: '', kesLow: 0, kesHigh: 0, billing: '/project', description: '', popular, features: [] };
}

function emptyEventPkg(id) {
  return { id, name: '', kesLow: 0, kesHigh: 0, bestFor: '', popular: false, elite: false, includes: [] };
}

// ── sub-components ────────────────────────────────────────────────────────────

function FeatureList({ features, onChange }) {
  const add = () => onChange([...features, '']);
  const remove = (i) => onChange(features.filter((_, idx) => idx !== i));
  const edit = (i, v) => { const a = [...features]; a[i] = v; onChange(a); };
  return (
    <div className="feature-list">
      {features.map((f, i) => (
        <div key={i} className="feature-row">
          <input className="pm-input" value={f} onChange={(e) => edit(i, e.target.value)} placeholder="Feature text" />
          <button type="button" className="pm-icon-btn danger" onClick={() => remove(i)} title="Remove"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="pm-add-btn" onClick={add}><Plus size={13} /> Add Feature</button>
    </div>
  );
}

function IncludesList({ items, onChange }) {
  const add = () => onChange([...items, '']);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const edit = (i, v) => { const a = [...items]; a[i] = v; onChange(a); };
  return (
    <div className="feature-list">
      {items.map((f, i) => (
        <div key={i} className="feature-row">
          <input className="pm-input" value={f} onChange={(e) => edit(i, e.target.value)} placeholder="Included item" />
          <button type="button" className="pm-icon-btn danger" onClick={() => remove(i)} title="Remove"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="pm-add-btn" onClick={add}><Plus size={13} /> Add Item</button>
    </div>
  );
}

function PackageCard({ pkg, onChange }) {
  const set = (field, val) => onChange({ ...pkg, [field]: val });
  return (
    <div className={`pm-pkg-card ${pkg.popular ? 'pm-popular' : ''}`}>
      <div className="pm-pkg-card-header">
        <span className="pm-pkg-label">{pkg.popular ? 'Most Popular' : 'Package'}</span>
        <label className="pm-checkbox-label">
          <input type="checkbox" checked={!!pkg.popular} onChange={(e) => set('popular', e.target.checked)} />
          Mark as Most Popular
        </label>
      </div>

      <div className="pm-field-row">
        <div className="pm-field">
          <label>Package Name</label>
          <input className="pm-input" value={pkg.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Basic Edit" />
        </div>
        <div className="pm-field pm-field-sm">
          <label>Billing Unit</label>
          <input className="pm-input" value={pkg.billing} onChange={(e) => set('billing', e.target.value)} placeholder="/project" />
        </div>
      </div>

      <div className="pm-field-row">
        <div className="pm-field">
          <label>Price From (KES)</label>
          <input className="pm-input" type="number" value={pkg.kesLow} onChange={(e) => set('kesLow', Number(e.target.value))} placeholder="5000" />
        </div>
        <div className="pm-field">
          <label>Price To (KES)</label>
          <input className="pm-input" type="number" value={pkg.kesHigh} onChange={(e) => set('kesHigh', Number(e.target.value))} placeholder="8000" />
        </div>
      </div>

      <div className="pm-field">
        <label>Short Description</label>
        <input className="pm-input" value={pkg.description} onChange={(e) => set('description', e.target.value)} placeholder="One-line description shown on card" />
      </div>

      <div className="pm-field">
        <label>Features</label>
        <FeatureList features={pkg.features || []} onChange={(f) => set('features', f)} />
      </div>
    </div>
  );
}

function CategorySection({ cat, onChange }) {
  const setField = (field, val) => onChange({ ...cat, [field]: val });
  const setPkg = (i, pkg) => {
    const pkgs = [...cat.packages];
    pkgs[i] = pkg;
    onChange({ ...cat, packages: pkgs });
  };

  return (
    <div className="pm-category">
      <div className="pm-category-meta">
        <div className="pm-field">
          <label>Category Name</label>
          <input className="pm-input" value={cat.name} onChange={(e) => setField('name', e.target.value)} />
        </div>
        <div className="pm-field">
          <label>Category Description</label>
          <textarea className="pm-textarea" rows={2} value={cat.description} onChange={(e) => setField('description', e.target.value)} />
        </div>
      </div>
      <div className="pm-pkgs-row">
        {cat.packages.map((pkg, i) => (
          <PackageCard key={i} pkg={pkg} onChange={(p) => setPkg(i, p)} />
        ))}
      </div>
    </div>
  );
}

function EventPackageCard({ ep, onChange }) {
  const set = (field, val) => onChange({ ...ep, [field]: val });
  return (
    <div className={`pm-pkg-card ${ep.popular ? 'pm-popular' : ''} ${ep.elite ? 'pm-elite' : ''}`}>
      <div className="pm-pkg-card-header">
        <span className="pm-pkg-label">{ep.elite ? 'Elite' : ep.popular ? 'Most Popular' : 'Package'}</span>
        <div className="pm-badges-row">
          <label className="pm-checkbox-label">
            <input type="checkbox" checked={!!ep.popular} onChange={(e) => set('popular', e.target.checked)} />
            Most Popular
          </label>
          <label className="pm-checkbox-label">
            <input type="checkbox" checked={!!ep.elite} onChange={(e) => set('elite', e.target.checked)} />
            Elite
          </label>
        </div>
      </div>

      <div className="pm-field">
        <label>Package Name</label>
        <input className="pm-input" value={ep.name} onChange={(e) => set('name', e.target.value)} />
      </div>

      <div className="pm-field-row">
        <div className="pm-field">
          <label>Price From (KES)</label>
          <input className="pm-input" type="number" value={ep.kesLow} onChange={(e) => set('kesLow', Number(e.target.value))} />
        </div>
        <div className="pm-field">
          <label>Price To (KES)</label>
          <input className="pm-input" type="number" value={ep.kesHigh} onChange={(e) => set('kesHigh', Number(e.target.value))} />
        </div>
      </div>

      <div className="pm-field">
        <label>Best For</label>
        <input className="pm-input" value={ep.bestFor} onChange={(e) => set('bestFor', e.target.value)} placeholder="Weddings, corporate events…" />
      </div>

      <div className="pm-field">
        <label>Includes</label>
        <IncludesList items={ep.includes || []} onChange={(v) => set('includes', v)} />
      </div>
    </div>
  );
}

function AddOnsSection({ addOns, onChange }) {
  const add = () => onChange([...addOns, { item: '', price: '' }]);
  const remove = (i) => onChange(addOns.filter((_, idx) => idx !== i));
  const edit = (i, field, val) => {
    const a = [...addOns];
    a[i] = { ...a[i], [field]: val };
    onChange(a);
  };
  return (
    <div className="pm-addons">
      {addOns.map((a, i) => (
        <div key={i} className="pm-addon-row">
          <input className="pm-input" value={a.item} onChange={(e) => edit(i, 'item', e.target.value)} placeholder="Add-on name" />
          <input className="pm-input pm-field-sm" value={a.price} onChange={(e) => edit(i, 'price', e.target.value)} placeholder="KES 3,000" />
          <button type="button" className="pm-icon-btn danger" onClick={() => remove(i)} title="Remove"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" className="pm-add-btn" onClick={add}><Plus size={13} /> Add Add-On</button>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

const TABS = [
  'Video Editing', 'Photo Editing', 'DJing', 'Photography',
  'Videography', 'Graphic Design', 'Web Design', 'PA System Rental',
  'Event Decoration', 'Event Packages', 'Add-Ons',
];

const PackagesManager = () => {
  const [config, setConfig] = useState(null);
  const [original, setOriginal] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    packagesAPI.get()
      .then((data) => { setConfig(data); setOriginal(JSON.stringify(data)); })
      .catch(() => showError('Failed to load packages config'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await packagesAPI.update(config);
      setOriginal(JSON.stringify(config));
      showSuccess('Packages saved successfully!');
    } catch {
      showError('Failed to save packages');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (original) setConfig(JSON.parse(original));
  };

  const setCat = (i, cat) => {
    const cats = [...config.serviceCategories];
    cats[i] = cat;
    setConfig({ ...config, serviceCategories: cats });
  };

  const setEventPkg = (i, ep) => {
    const eps = [...config.eventPackages];
    eps[i] = ep;
    setConfig({ ...config, eventPackages: eps });
  };

  const isDirty = config && original && JSON.stringify(config) !== original;

  if (loading) return <div className="pm-loading">Loading packages configuration…</div>;
  if (!config) return <div className="pm-loading">Failed to load config.</div>;

  const serviceCatIndex = activeTab; // 0–8 = service categories
  const isEventPkgTab = activeTab === 9;
  const isAddOnsTab = activeTab === 10;

  return (
    <div className="pm-container">
      {/* Header */}
      <div className="pm-header">
        <div>
          <h1 className="pm-title">Packages Manager</h1>
          <p className="pm-subtitle">Edit pricing packages displayed on the public Packages page.</p>
        </div>
        <div className="pm-header-actions">
          {isDirty && (
            <button className="pm-btn pm-btn-ghost" onClick={handleReset} disabled={saving}>
              <RotateCcw size={16} /> Discard
            </button>
          )}
          <button className="pm-btn pm-btn-primary" onClick={handleSave} disabled={saving || !isDirty}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {isDirty && <div className="pm-unsaved-banner">You have unsaved changes.</div>}

      {/* Tab bar */}
      <div className="pm-tabs">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            className={`pm-tab ${activeTab === i ? 'active' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="pm-content">
        {!isEventPkgTab && !isAddOnsTab && config.serviceCategories[serviceCatIndex] && (
          <CategorySection
            cat={config.serviceCategories[serviceCatIndex]}
            onChange={(cat) => setCat(serviceCatIndex, cat)}
          />
        )}

        {isEventPkgTab && (
          <div className="pm-category">
            <div className="pm-category-meta">
              <p className="pm-section-note">These are the bundled event packages (no decor) shown at the bottom of the Packages page.</p>
            </div>
            <div className="pm-pkgs-row pm-ep-row">
              {config.eventPackages.map((ep, i) => (
                <EventPackageCard key={ep.id || i} ep={ep} onChange={(e) => setEventPkg(i, e)} />
              ))}
            </div>
          </div>
        )}

        {isAddOnsTab && (
          <div className="pm-category">
            <div className="pm-category-meta">
              <p className="pm-section-note">Optional add-ons shown below the Event Packages section.</p>
            </div>
            <AddOnsSection addOns={config.addOns || []} onChange={(v) => setConfig({ ...config, addOns: v })} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PackagesManager;
